import { AccountCircle, AttachMoney, CreditCard, PeopleAlt, TrendingUp } from '@mui/icons-material';
import { Box, Card, CardContent, Grid, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import Dinero from 'dinero.js';
import React from 'react';
import { OnlyFansDailyStats, OnlyFansPayout } from '../../../../../../hooks/useMetricsDailyStats';
import { DashboardProps } from '../SextforceMetricsAccountValueStats';
import ExportButtons from './ExportButtons';

// Helper functions (from original code)
const createDinero = (amount: number): Dinero.Dinero => {
    const cents = Math.round(amount * 100);
    return Dinero({ amount: cents, currency: 'USD' });
};

const formatDinero = (money: Dinero.Dinero, withFraction: boolean): string => {
    return withFraction ? money.toFormat(`$0,0.00`) : money.toFormat(`$0,0`);
};

const formatNumber = (num: number): string => {
    return num.toLocaleString();
};

// Calculate the lifetime value
const calculateLifetimeValue = (
    data: OnlyFansDailyStats[],
    latestEarnings: OnlyFansPayout.PayoutsStats | null | undefined,
): {
    activeFans: number;
    expiredFans: number;
    all: number;
} => {
    if (!latestEarnings) return { activeFans: 0, expiredFans: 0, all: 0 };

    // Sum up all earnings
    let totalEarnings = 0;

    if (latestEarnings?.list?.total?.all?.total_net) {
        totalEarnings += latestEarnings.list.total.all.total_net;
    }

    // Get the total number of active subscribers
    const latestData = data[0];
    const totalSubscribersActive = latestData.counters?.subscribers.active || 1;
    const totalSubscribersExpired = latestData.counters?.subscribers.expired || 1;
    const totalSubscribersAll = latestData.counters?.subscribers.all || 1;

    // LTV = Total Earnings / Number of Subscribers
    return {
        activeFans: totalSubscribersActive > 0 ? totalEarnings / totalSubscribersActive : 0,
        expiredFans: totalSubscribersExpired > 0 ? totalEarnings / totalSubscribersExpired : 0,
        all: totalSubscribersAll > 0 ? totalEarnings / totalSubscribersAll : 0,
    };
};

// Calculate retention rate
const calculateRetentionRate = (data: OnlyFansDailyStats[]): number => {
    if (!data || data.length < 30) return 0;

    const day30 = data[data.length - 1];
    const today = data[0];

    if (!day30.counters?.subscribers.all || !today.counters?.subscribers.all) return 0;

    const initialSubscribers = day30.counters.subscribers.all;
    const currentActive = today.counters.subscribers.active;

    return initialSubscribers > 0 ? (currentActive / initialSubscribers) * 100 : 0;
};

const KeyMetricsCards: React.FC<DashboardProps> = ({
    subscriber,
    data,
    isLoading,
    latestEarnings,
    isLatestEarningsLoading,
    amountType,
    handleSetAmountType,
}) => {
    const theme = useTheme();

    if (isLoading) {
        return (
            <Grid container spacing={2}>
                {Array.from(new Array(9)).map((_, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                        <Card elevation={3} sx={{ height: '100%' }}>
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <Skeleton variant="circular" width={40} height={40} />
                                    <Stack direction={'column'} ml={1} width="70%">
                                        <Skeleton variant="text" width="80%" />
                                        <Skeleton variant="text" width="40%" />
                                    </Stack>
                                </Box>
                                <Skeleton variant="text" width="60%" height={40} />
                                <Skeleton variant="text" width="80%" />
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        );
    }

    if (!data || data.length === 0) {
        return (
            <Card elevation={3} sx={{ height: '100%' }}>
                <CardContent>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} mb={1}>
                        <Typography variant="h6" component="div">
                            Key Metrics
                        </Typography>
                    </Stack>
                    <Typography variant="body2" component="div" color="textSecondary">
                        No data available
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    const latestData = data[0];
    const counters = latestData.counters;
    const earnings = latestEarnings;
    const performerTop = latestData.performerTop;

    // Create Dinero object for total earnings
    const totalEarningsAmountNet = earnings?.list?.total?.all?.total_net || 0;
    const totalEarningsDineroNet = createDinero(totalEarningsAmountNet);
    const lifetimeValue = calculateLifetimeValue(data, latestEarnings);
    const lifetimeValueDineroFansAll = createDinero(lifetimeValue.all);

    const activeSubscribers = counters?.subscribers.active || 0;
    const totalSubscribers = counters?.subscribers.all || 0;
    const retentionRate = calculateRetentionRate(data);

    // Last 30 days
    const totalVisitorsLast30Days = data.reduce((sum, day) => sum + (day.profileVisitors?.visitors?.visitors.total || 0), 0) || 0;

    const newSubscriptionsLast30Days =
        data.reduce((sum, day) => sum + (day.fansSubscriptions?.fans?.subscriptions?.new?.total || 0), 0) || 0;
    const conversionRate = totalVisitorsLast30Days > 0 ? (newSubscriptionsLast30Days / totalVisitorsLast30Days) * 100 : 0;

    const fontSize = 32;

    const metrics = [
        {
            title: 'Total Earnings',
            subtitle: 'net',
            value: formatDinero(totalEarningsDineroNet, true),
            icon: <AttachMoney sx={{ fontSize, color: theme.palette.primary.main }} />,
            description: 'Net lifetime earnings',
        },
        {
            title: 'Profile Views',
            subtitle: 'Last 30 Days',
            value: formatNumber(totalVisitorsLast30Days),
            icon: <AccountCircle sx={{ fontSize, color: theme.palette.info.main }} />,
            description: 'Total profile views',
        },
        {
            title: 'Lifetime Value',
            subtitle: 'All Fans',
            value: formatDinero(lifetimeValueDineroFansAll, true),
            icon: <CreditCard sx={{ fontSize, color: theme.palette.success.main }} />,
            description: 'Average revenue per all fans',
        },
        {
            title: 'Ranking',
            subtitle: 'Top %',
            value: `${formatNumber(performerTop || 0)}%`,
            icon: <TrendingUp sx={{ fontSize, color: theme.palette.success.main }} />,
            description: 'Top % of performers',
        },
        {
            title: 'Conversion Rate',
            subtitle: 'Last 30 Days',
            value: `${conversionRate.toFixed(2)}%`,
            icon: <TrendingUp sx={{ fontSize, color: theme.palette.info.main }} />,
            description: 'From visitors to subscribers',
        },
        {
            title: 'Retention Rate',
            subtitle: '',
            value: `${retentionRate.toFixed(1)}%`,
            icon: <TrendingUp sx={{ fontSize, color: theme.palette.info.main }} />,
            description: '30-day retention rate',
        },
        {
            title: 'Active Fans',
            subtitle: '',
            value: formatNumber(activeSubscribers),
            icon: <PeopleAlt sx={{ fontSize, color: theme.palette.success.main }} />,
            description: `${((activeSubscribers / totalSubscribers) * 100).toFixed(2)}% of total subscribers`,
        },
        {
            title: 'Expired Fans',
            subtitle: '',
            value: formatNumber(counters?.subscribers.expired || 0),
            icon: <PeopleAlt sx={{ fontSize, color: theme.palette.secondary.main }} />,
            description: `${(((counters?.subscribers.expired || 0) / totalSubscribers) * 100).toFixed(2)}% of total subscribers`,
        },
    ];

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6">Key Metrics</Typography>
                <div>
                    <ExportButtons
                        data={metrics.map(metric => ({
                            title: `${metric.title}${metric.subtitle ? ` (${metric.subtitle})` : ''}`,
                            value: metric.value,
                            description: metric.description,
                        }))}
                        componentType="keyMetrics"
                        filename={`${subscriber.username}_sub_Key_Metrics`}
                    />
                </div>
            </Stack>
            <Grid container spacing={2}>
                {metrics.map((metric, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                        <Card elevation={3} sx={{ height: '100%' }}>
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={1}>
                                    {metric.icon}
                                    <Stack direction={'column'} ml={1}>
                                        <Typography variant="h6" component="div" color="textSecondary">
                                            {metric.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            component="div"
                                            color="textSecondary"
                                            textTransform={'uppercase'}
                                            sx={{ top: '-6px', position: 'relative' }}
                                        >
                                            {metric.subtitle || <div>&nbsp;</div>}
                                        </Typography>
                                    </Stack>
                                </Box>
                                <Typography variant="h5" component="div" fontWeight="bold">
                                    {metric.value}
                                </Typography>
                                <Typography variant="body2" component="div" color="textSecondary" mt={1}>
                                    {metric.description}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default KeyMetricsCards;
